import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "../../../hooks";
import { Typo } from "../../../Typo";
import {
  MobileSortBottomSheet,
  SortOption,
} from "../../Common/controls/MobileSortBottomSheet";
import { ReturnedTranslatedContentClientsPageHook } from "../../types";
import { useClientFundedNotTradedReportContext } from "../providers/ClientFundedNotTradedProvider";

type ClientFundedNotTradedReportSortProps = {
  isMobileBottomSheetEnable: boolean;
  translations: ReturnedTranslatedContentClientsPageHook;
};

export const ClientFundedNotTradedReportSort = ({
  isMobileBottomSheetEnable,
  translations,
}: ClientFundedNotTradedReportSortProps) => {
  const {
    state: { sortBy, sortOrder },
    actions: { setSort },
    runResource,
  } = useClientFundedNotTradedReportContext();
  const isMobile = useIsMobile();

  const { t } = useTranslation();

  const sortOptions: SortOption[] = useMemo(
    () => [
      {
        id: "depositedAt",
        label:
          translations.translatedContent.fundedNotTradedTab
            .fundedNotTradedColumnLabels.depositDate,
      },
      {
        id: "depositAmount",
        label:
          translations.translatedContent.fundedNotTradedTab
            .fundedNotTradedColumnLabels.depositAmount,
      },
    ],
    [translations],
  );

  const getOrderLabels = useMemo(
    () => (currentSortBy: string) => {
      if (currentSortBy === "depositedAt") {
        return {
          descLabel: t("clientsPage_sort_Bottomsheet_newestFirst"),
          ascLabel: t("clientsPage_sort_Bottomsheet_oldestFirst"),
        };
      }
      return {
        descLabel: t("clientsPage_sort_Bottomsheet_highToLow"),
        ascLabel: t("clientsPage_sort_Bottomsheet_lowToHigh"),
      };
    },
    [t],
  );

  if (!isMobileBottomSheetEnable || !isMobile) {
    return null;
  }

  return (
    <div className="sticky top-[136px] z-10 -mb-4 bg-surface-primary pb-2">
      <div className="flex h-10 items-center gap-6">
        <div className="min-w-0 flex-1">
          <MobileSortBottomSheet
            sortOptions={sortOptions}
            selectedSortBy={sortBy ?? "depositedAt"}
            selectedSortOrder={sortOrder ?? "desc"}
            onApply={(newSortBy, newSortOrder) =>
              setSort(newSortBy, newSortOrder)
            }
            getOrderLabels={getOrderLabels}
          />
        </div>
        <div className="flex shrink-0 flex-row items-center">
          <button
            onClick={() => runResource("csv")}
            className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-surface-tonal-default px-3 py-2"
          >
            <Typo type="labelSM" classes="whitespace-nowrap">
              {translations.translatedContent.exportCSVText}
            </Typo>
          </button>
        </div>
      </div>
    </div>
  );
};
